import React, { useContext } from "react";
import styled from "styled-components/macro";
import Flex from "../../shared/Flex";
import Button from "../../shared/Button";
import { forMinWidth } from "../../../theme/breakpoints";
import { ReactivationConfig, reactivationRules } from "./reactivationRules";
import * as Actions from "../../../store/actions";
import { TicketContext } from "./TicketContext";
import { ticketOperationKey, UIOperationState, useOperation } from "../../../store/operation";
import { ticketNotificationKey } from "../../../store/actionsHandler";
import { ensureSignedIn } from "../../../utils/webPage";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootState";
import { ProblemStates } from "../../../constants/feedback";
import { Link } from "react-router-dom";
import { AppPaths } from "../../../constants/urls";
import { EntryValue, UrlParam } from "../../../constants/urlParams";
import { NodeVisibility } from "../../../store/actions";
import { Icon } from "@fluentui/react/lib/Icon";
import { ChildPost, ReactionType } from "../../../RootPost";

export interface ReactivateProps {
  state: string;
  solution: ChildPost;
  isReadonly?: boolean;
  reactedYes: boolean;
  reactedNo: boolean;
  onReactNoClicked: (solution: ChildPost, rule: ReactivationConfig) => void;

  className?: string;
}

export default function Reactivate(props: ReactivateProps): JSX.Element {
  const ticketDetailsCtx = useContext(TicketContext);
  const ticket = ticketDetailsCtx.ticketDetails.ticket?.data;
  const signInState = useSelector((x: RootState) => x.appState.signInState);
  const isUserBanned = useSelector((x: RootState) => x.appState.isUserBanned);
  const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);

  const solutionOpKey = ticketOperationKey(ticket?.id, props.solution.id);
  const solutionOperation = useOperation(solutionOpKey);

  let reactYesCount = 0;
  let reactNoCount = 0;

  props.solution.children.forEach(x => {
    if (x.reaction === "reactYes") {
      reactYesCount++;
    }
    if (x.reaction === "reactNo") {
      reactNoCount++;
    }
  });

  const rule = reactivationRules[props.state];

  if (!rule) {
    return null;
  }

  const onReactYesClicked = (): void => {
    if (ensureSignedIn(signInState, ticketNotificationKey(ticket?.id), "Please sign in to submit this response.")) {
      solutionOperation.setState(UIOperationState.InProgress);
      Actions.reactivate(ticket.id, solutionOpKey, props.solution.id, ReactionType.react_yes, NodeVisibility.all, null);
    }
  }

  const onReactNoClicked = (): void => {
    props.onReactNoClicked(props.solution, rule);
  }

  return <ReactivateWrapper column densityVer="normal" className={props.className ?? ""}>
    <div className="separator-line"></div>
    {!props.isReadonly && <div className="title"><span>{rule.title}</span></div>}

    {props.isReadonly && ticket.state === ProblemStates.fixed && (
      <div className="title"><span>{"This issue has been closed for over 90 days. If we haven't resolved this issue for you, please "}
        <Link
          to={`/${AppPaths.knownSlugVisualStudio}${AppPaths.report}`}
        >report a new problem</Link>
      </span></div>
    )}

    <Flex densityHor="relaxed" className="buttons">
      <ReactivationButton
        title={rule.yesTitle}
        subTitle={rule.yesSubTitle}
        voteCount={reactYesCount}
        voteIconName="SortUp"
        isDisabled={isInReadOnlyMode || props.isReadonly || props.reactedYes || isUserBanned}
        onClick={onReactYesClicked}
      />

      <ReactivationButton
        title={rule.noTitle}
        subTitle={rule.noSubTitle}
        voteCount={reactNoCount}
        voteIconName="SortDown"
        isDisabled={isInReadOnlyMode || props.isReadonly || props.reactedNo || isUserBanned}
        onClick={onReactNoClicked}
      />
    </Flex>
  </ReactivateWrapper>
}

export interface ReactivationButtonProps {
  title: string;
  subTitle?: string;
  voteIconName: string;
  voteCount?: number;
  isDisabled?: boolean;
  onClick?: () => void;
}

const ReactivationButton = (props: ReactivationButtonProps): JSX.Element => {
  return <ReactivationButtonWrapper buttonType="normal" disabled={props.isDisabled} onClick={props.onClick}>
    <Flex densityHor="relaxed">
      <Flex column alignItems="flex-start" className="title-container">
        <div>{props.title}</div>
        {props.subTitle && <div className="detail">{props.subTitle}</div>}
      </Flex>
      <Flex alignItems="center" className="vote-count">
        <span>{props.voteCount}</span>
        <Icon iconName={props.voteIconName} />
      </Flex>
    </Flex>
  </ReactivationButtonWrapper>
}

const ReactivationButtonWrapper = styled(Button)`
  font-size: 1.6rem;
  min-height: 7.5rem;

  && {
    padding: 1.5rem;
    border: solid 1px var(--color-on-surface);
    background-color: var(--color-surface);
    color: var(--color-on-surface);
  }

  ${forMinWidth.md`
    .title-container {
      min-width: 9rem;
    }
  `}

  .detail {
    font-size: 1.2rem;
    color: var(--color-surface-dark2);
  }

  .vote-count {
    color: var(--color-on-surface);

    i {
      font-size: 1.4rem;
    }
  }

  &:disabled, .detail:disabled {
    color: var(--color-on-surface-light);
  }
`;

const ReactivateWrapper = styled(Flex)`
  padding-top: 2rem;

  .separator-line {
    border-top: solid 1px var(--color-on-surface-lighter);
  }

  .title {
    font-size: 1.6rem;
    font-weight: 600;

    >span {
      display: inline-block;
      max-width: 60rem;
    }
  }

  .buttons{
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 1rem;
    margin-left: 0;

    > * + * {
      margin-top: 1rem;
      margin-left: 0;
    }
  }

  ${forMinWidth.md`
    .buttons {
      flex-direction: row;
      align-items: flex-start;

      > * + * {
        margin-top: 0;
        margin-left: 3rem;
      }
    }
  `}
`;
